/**
 * Uppladdning av E.on CSV-export.
 *
 * Användaren laddar upp filen, vi parsar lokalt (anläggnings-id och
 * andra metadata-fält strippas i parsern), användaren bekräftar
 * prisområde och säkringsstorlek, och vi berikar med spotpriser från
 * backend innan vi skickar UserData uppåt till Simulator.
 */

import { useState } from "react";
import {
  parseEonCsv,
  CsvParseError,
  type ParseResult,
} from "../data-sources/eonCsvParser";
import type { UserData, PriceArea } from "../types";
import { enrichCsvHours, ApiError } from "../api/client";
import { BatteryLoader } from "./BatteryLoader";

interface CsvUploadProps {
  onDataLoaded: (data: UserData) => void;
  onCancel: () => void;
}

type Stage =
  | "select-file"
  | "preview"
  | "enriching"
  | "error";

const MONTH_NAMES_SV = [
  "jan", "feb", "mar", "apr", "maj", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

export default function CsvUpload({ onDataLoaded, onCancel }: CsvUploadProps) {
  const [stage, setStage] = useState<Stage>("select-file");
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [priceArea, setPriceArea] = useState<PriceArea>("SE3");
  const [error, setError] = useState<string | null>(null);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    try {
      const text = await file.text();
      const result = parseEonCsv(text);
      if (result.hours.length < 24) {
        setError(
          "Filen innehåller mindre än ett dygn data. Ladda upp en längre period.",
        );
        setStage("error");
        return;
      }
      setParseResult(result);
      setStage("preview");
    } catch (err) {
      if (err instanceof CsvParseError) {
        setError(err.message);
      } else {
        setError("Kunde inte läsa filen. Kontrollera att det är en E.on CSV-export.");
      }
      setStage("error");
    }
  }

  async function handleConfirm() {
    if (!parseResult) return;
    setStage("enriching");
    setError(null);

    try {
      const enriched = await enrichCsvHours(
        priceArea,
        parseResult.hours.map((h) => ({
          from: h.from,
          importKWh: h.importKWh,
          exportKWh: h.exportKWh,
        })),
      );

      const userData: UserData = {
        metadata: {
          source: "eon-csv",
          fetchedAt: new Date().toISOString(),
          resolution: enriched.hours[0]?.quarterPrices ? "quarter" : "hour",
          quality: "measured",
          periodFrom: enriched.metadata.firstTimestamp,
          periodTo: enriched.metadata.lastTimestamp,
          hourCount: enriched.metadata.totalHours,
          totalImport: parseResult.annualImportKWh,
          totalExport: parseResult.annualExportKWh,
          hoursWithMissingPrice: enriched.metadata.hoursWithMissingPrice,
          // Syntetisk TibberHome så resten av appen kan plocka upp priceArea
          // och fuseSize utan att veta att datan inte kommer från Tibber.
          tibberHome: {
            id: `csv-${crypto.randomUUID()}`,
            nickname: null,
            address: { address1: null, postalCode: null, city: null },
            gridArea: null,
            priceArea,
          },
        },
        hours: enriched.hours.map((h) => ({
          from: h.from,
          importKWh: h.importKWh,
          exportKWh: h.exportKWh,
          spotPrice: h.spotPrice,
          ...(h.quarterPrices && { quarterPrices: h.quarterPrices }),
        })),
      };

      onDataLoaded(userData);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Kunde inte hämta spotpriser. Försök igen om en stund.";
      setError(message);
      setStage("error");
    }
  }

  // Räkna ut månadsfördelning för förhandsvisning
  const monthly = parseResult
    ? (() => {
        const sums = new Map<string, number>();
        for (const h of parseResult.hours) {
          const month = h.from.substring(0, 7);
          sums.set(month, (sums.get(month) ?? 0) + h.importKWh);
        }
        return Array.from(sums.entries()).sort(([a], [b]) => a.localeCompare(b));
      })()
    : [];

  if (stage === "select-file") {
    return (
      <section className="csv-upload">
        <h2>Ladda upp E.on CSV</h2>
        <p className="hint">
          Logga in på <a href="https://www.eon.se/mina-sidor" target="_blank" rel="noopener noreferrer">Mina sidor hos E.on</a>{" "}
          → välj "Mätvärden" → exportera som CSV. Vi parsar filen lokalt i
          din webbläsare. Anläggnings-ID och andra fält skickas aldrig till
          våra servrar.
        </p>
        <label className="csv-upload-button">
          <input
            type="file"
            accept=".csv,.CSV,text/csv"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <span>Välj fil</span>
        </label>
        <button type="button" className="link" onClick={onCancel}>
          Avbryt
        </button>
      </section>
    );
  }

  if (stage === "preview" && parseResult) {
    return (
      <section className="csv-upload">
        <h2>Förhandsvisning</h2>
        <dl className="csv-preview-summary">
          <dt>Period</dt>
          <dd>
            {parseResult.firstTimestamp.substring(0, 10)} —{" "}
            {parseResult.lastTimestamp.substring(0, 10)}
          </dd>
          <dt>Antal timmar</dt>
          <dd>{parseResult.hours.length.toLocaleString("sv-SE")}</dd>
          <dt>Totalt importerad el</dt>
          <dd>{parseResult.annualImportKWh.toLocaleString("sv-SE")} kWh</dd>
          {parseResult.annualExportKWh > 0 && (
            <>
              <dt>Totalt producerad el (sol)</dt>
              <dd>{parseResult.annualExportKWh.toLocaleString("sv-SE")} kWh</dd>
            </>
          )}
        </dl>

        <h3>Månadsfördelning</h3>
        <ul className="csv-month-list">
          {monthly.map(([m, kwh]) => {
            const monthIdx = parseInt(m.substring(5, 7), 10) - 1;
            return (
              <li key={m}>
                <span>{MONTH_NAMES_SV[monthIdx]} {m.substring(0, 4)}</span>
                <strong>{Math.round(kwh).toLocaleString("sv-SE")} kWh</strong>
              </li>
            );
          })}
        </ul>

        <div className="csv-upload-fields">
          <label>
            <span>Prisområde (kontrollera mot din elräkning)</span>
            <select
              value={priceArea}
              onChange={(e) => setPriceArea(e.target.value as PriceArea)}
            >
              <option value="SE1">SE1 Norra Norrland</option>
              <option value="SE2">SE2 Norra Sverige</option>
              <option value="SE3">SE3 Mellersta Sverige</option>
              <option value="SE4">SE4 Södra Sverige</option>
            </select>
          </label>
        </div>

        {parseResult.estimatedShare > 0.05 && (
          <p className="hint warning">
            {Math.round(parseResult.estimatedShare * 100)}% av mätvärdena är
            beräknade (inte uppmätta). Resultatet kan bli mindre exakt.
          </p>
        )}

        <div className="csv-upload-actions">
          <button type="button" onClick={handleConfirm}>
            Använd denna data
          </button>
          <button
            type="button"
            className="link"
            onClick={() => setStage("select-file")}
          >
            Välj annan fil
          </button>
        </div>
      </section>
    );
  }

  if (stage === "enriching") {
    return (
      <section className="csv-upload csv-upload-loading">
        <BatteryLoader size={120} label="Hämtar spotpriser" />
        <p className="hint">
          Matchar din timserie mot spotpriser för {priceArea}. Tar några
          sekunder.
        </p>
      </section>
    );
  }

  // error
  return (
    <section className="csv-upload">
      <h2>Något gick fel</h2>
      <p className="error">{error}</p>
      <button type="button" onClick={() => setStage("select-file")}>
        Försök igen
      </button>
      <button type="button" className="link" onClick={onCancel}>
        Avbryt
      </button>
    </section>
  );
}
